import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useEffectiveClientId } from '@/hooks/useAuth'
import type { Region, TopicWeight, Plan, Client } from '@/types/database'

/**
 * Regions and topics: the client's coverage configuration.
 *
 * Both are written through database functions rather than direct inserts, so
 * the plan cap and the seed charge cannot be bypassed. The types say `never`
 * for those inserts, so an attempt fails at compile time too.
 */

export function usePlan() {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['plan', clientId],
    enabled: !!clientId,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<Plan | null> => {
      const { data: client, error: cErr } = await supabase
        .from('clients').select('plan_id, onboarded_at').eq('id', clientId!).single()
      if (cErr) throw cErr
      if (!client?.plan_id) return null
      const { data, error } = await supabase
        .from('plans').select('*').eq('id', client.plan_id).single()
      if (error) throw error
      return data as Plan
    },
  })
}

/** Null onboarded_at means the client is still in free setup. */
export function useOnboardingState() {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['onboarding', clientId],
    enabled: !!clientId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clients').select('onboarded_at').eq('id', clientId!).single()
      if (error) throw error
      return { onboarded: !!(data as Pick<Client, 'onboarded_at'>).onboarded_at }
    },
  })
}

export function useCompleteOnboarding() {
  const clientId = useEffectiveClientId()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('clients')
        .update({ onboarded_at: new Date().toISOString() })
        .eq('id', clientId!)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding', clientId] })
    },
  })
}

// ---- Regions ----

export function useRegions() {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['regions', clientId],
    enabled: !!clientId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('regions').select('*').eq('client_id', clientId!)
        .order('created_at', { ascending: true })
      if (error) throw error
      return data as Region[]
    },
  })
}

export class CapReachedError extends Error {
  constructor(message: string) { super(message); this.name = 'CapReachedError' }
}

export function useAddRegion() {
  const clientId = useEffectiveClientId()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (args: { name: string; scope?: Region['scope'] }) => {
      const { data, error } = await supabase.rpc('add_region', {
        p_client_id: clientId!,
        p_name: args.name,
        p_scope: args.scope ?? 'city',
      })
      if (error) {
        if (/limit reached/i.test(error.message)) throw new CapReachedError(error.message)
        throw error
      }
      return data as string
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['regions', clientId] })
      // Adding costs seeds once onboarding is done.
      queryClient.invalidateQueries({ queryKey: ['seed-balance', clientId] })
    },
  })
}

export function useRemoveRegion() {
  const clientId = useEffectiveClientId()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('regions').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['regions', clientId] }),
  })
}

// ---- Topics ----

export function useTopics() {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['topics', clientId],
    enabled: !!clientId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('topic_weights').select('*').eq('client_id', clientId!)
      if (error) throw error
      return data as TopicWeight[]
    },
  })
}

/**
 * Adds a topic, or reweights one already held.
 *
 * Adding a NEW topic costs 5 seeds after onboarding and is capped by plan.
 * Reweighting something already held is free, because it is not a new slot.
 */
export function useSetTopic() {
  const clientId = useEffectiveClientId()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (args: { topic: string; weight?: number }) => {
      const { data, error } = await supabase.rpc('set_topic', {
        p_client_id: clientId!,
        p_topic: args.topic,
        p_weight: args.weight ?? 0.3,
      })
      if (error) {
        if (/limit reached/i.test(error.message)) throw new CapReachedError(error.message)
        throw error
      }
      return data as string
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['topics', clientId] })
      queryClient.invalidateQueries({ queryKey: ['seed-balance', clientId] })
    },
  })
}

export function useRemoveTopic() {
  const clientId = useEffectiveClientId()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('topic_weights').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['topics', clientId] }),
  })
}
